import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, frame, cancelFrame } from 'framer-motion';
import { sections, splitDescription } from '../data/entries';
import { parsePost } from '../data/posts';
import { arrival, linkTo } from '../hooks/useRoute';
import SwapText from './SwapText';
import './styles/Experiences.css';

// How long the cursor has to rest on a row before it opens. Without it,
// sweeping down this dense list flicks every row open on the way past.
const HOVER_OPEN_DELAY_MS = 100;

// A stat in a detail line: 500k+, ≈23m+, 96%+, 50-hour, 1tb, 3.86. The
// character before it is captured rather than looked behind at, which older
// Safari can't parse. A year is a date, not a stat.
const STAT = /(^|[^\w.])(≈?\d[\d,.]*(?:k|m|tb|%)?\+?(?:-hour)?)(?!\w)/gi;
const YEAR = /^(?:19|20)\d\d$/;

// Marks what a reader scans a detail line for: its lead, a label like
// "courses:" or else the opening verb, and its stats. Styled in
// Experiences.css.
function emphasize(text) {
  const colon = text.indexOf(':');
  const leadEnd = colon > 0 && colon < 24 ? colon + 1 : text.search(/\s|$/);
  const marks = [[0, leadEnd, 'row-lead']];
  for (const m of text.matchAll(STAT)) {
    const start = m.index + m[1].length;
    if (start >= leadEnd && !YEAR.test(m[2])) marks.push([start, start + m[2].length, 'row-stat']);
  }
  const parts = [];
  let at = 0;
  marks.forEach(([start, end, className]) => {
    if (start > at) parts.push(text.slice(at, start));
    parts.push(
      <span key={start} className={className}>
        {text.slice(start, end)}
      </span>
    );
    at = end;
  });
  parts.push(text.slice(at));
  return parts;
}

// Keeps `el` at the same place on screen while the rows above it change height,
// for the length of the open and close animations.
//
// Scrolling the page every frame to cancel the drift shakes it on iOS Safari,
// which applies a script's scroll a frame late: each correction lands after the
// layout it was chasing has moved on, and the page jumps tens of pixels back
// and forth until the animation ends. So the drift is cancelled by shifting the
// work page with a transform instead, set in framer-motion's post-render step,
// after that frame's animated heights and before paint, so the two land
// together on every browser. When the animation is over, the shift is traded
// for one scroll of the same distance. The browser's own scroll anchoring is
// off meanwhile, or it would cancel the same drift a second time.
let releaseHold = null;

function holdInPlace(el, duration = 450) {
  const page = el?.closest('.work-page');
  if (!page) return;
  releaseHold?.();
  const root = document.documentElement;
  const start = el.getBoundingClientRect().top;
  const until = performance.now() + duration;
  let shift = 0;
  root.style.overflowAnchor = 'none';

  const release = () => {
    cancelFrame(correct);
    page.style.transform = '';
    window.scrollBy(0, -shift);
    root.style.overflowAnchor = '';
    releaseHold = null;
  };
  function correct() {
    // Where the row sits in the layout, with the current shift taken back out.
    const top = el.getBoundingClientRect().top - shift;
    shift = start - top;
    page.style.transform = Math.abs(shift) >= 0.5 ? `translateY(${shift}px)` : '';
    if (performance.now() >= until) release();
  }
  releaseHold = release;
  frame.postRender(correct, true);
}

// Seconds between one element's entrance and the next, down the page.
const STAGGER = 0.06;

// Entrance: text fades in while the rules and dotted leaders draw themselves
// from the left. Each element receives its position down the page as
// `custom`, so the stagger runs on down the list.
const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i) => ({ opacity: 1, transition: { duration: 0.5, delay: i * STAGGER } }),
};

const drawIn = {
  hidden: { scaleX: 0 },
  visible: (i) => ({
    scaleX: 1,
    transition: { duration: 0.6, ease: [0.65, 0, 0.35, 1], delay: i * STAGGER + 0.1 },
  }),
};

function useCanHover() {
  const query = '(hover: hover) and (pointer: fine)';
  const [canHover, setCanHover] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setCanHover(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return canHover;
}

// A wrapped title's box keeps the full width it was given, wider than its last
// line, so a leader starting at the box's edge would leave a gap after the
// text. This measures that gap — from the box's edge back to `endRef`, an
// empty marker after the last word — so the leader can be pulled back over it.
function useLeaderReach(titleRef, endRef) {
  const [reach, setReach] = useState(0);
  useLayoutEffect(() => {
    const title = titleRef.current;
    const end = endRef.current;
    if (!title || !end) return undefined;
    const measure = () => {
      const gap = title.getBoundingClientRect().right - end.getBoundingClientRect().right;
      setReach(gap >= 1 ? Math.round(gap) : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    window.addEventListener('resize', measure);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [titleRef, endRef]);
  return reach;
}

const leaderStyle = (reach) => (reach ? { marginLeft: -reach } : undefined);

// A company held more than once (an internship, then a full-time role) is
// shown once, as a line of its own over its roles, the way LinkedIn does.
// Titles are "role @ company"; each company's roles gather where its latest
// one sits, newest first, as the entries are ordered.
function groupRoles(items) {
  const groups = [];
  const byCompany = new Map();
  items.forEach((item, i) => {
    const at = item.title.lastIndexOf(' @ ');
    const company = at >= 0 ? item.title.slice(at + 3) : null;
    const member = { item, i, role: at >= 0 ? item.title.slice(0, at) : item.title };
    const group = company && byCompany.get(company);
    if (group) {
      group.members.push(member);
      return;
    }
    const fresh = { company, members: [member] };
    groups.push(fresh);
    if (company) byCompany.set(company, fresh);
  });
  return groups;
}

// A role still to come has `starts` instead of a period: it sits a step back,
// on a hollow dot, with "incoming" and the month as its date. One whose
// period runs to "present" is the role I'm in, marked with a star
// (Experiences.css).
const periodOf = (entry) => (entry.starts ? `incoming ${entry.starts}` : entry.period);
const isCurrent = (entry) => !entry.starts && / – present$/.test(entry.period);

// `alwaysOpen`: the only entry in its section, so there's nothing to choose
// between and its details simply show. Its header sits under the title
// instead of taking the title's place.
function Row({
  entry,
  index,
  rowKey,
  isOpen,
  onOpen,
  onClose,
  onToggle,
  title = entry.title,
  arrived = false,
  alwaysOpen = false,
}) {
  const lastPointerType = useRef('mouse');
  const focusOpened = useRef(false);
  const headRef = useRef(null);
  const titleRef = useRef(null);
  const endRef = useRef(null);
  const openTimer = useRef(null);
  const bodyId = useId();
  const reach = useLeaderReach(titleRef, endRef);
  const { header, details } = splitDescription(entry.description);

  useEffect(() => () => clearTimeout(openTimer.current), []);

  // A mouse opens a row by resting on it and closes it by leaving. Touch has
  // no hover to leave — a tap emits a synthetic mouseenter but never a
  // matching mouseleave — so it toggles on tap instead, anywhere on the row.
  // Deciding from the pointer that started the interaction, rather than from
  // the device, keeps both working on a touchscreen laptop.
  const handlePointerEnter = (e) => {
    if (e.pointerType !== 'mouse' || !expandable) return;
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(onOpen, HOVER_OPEN_DELAY_MS);
  };
  const handlePointerLeave = (e) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(openTimer.current);
    onClose();
  };
  const handlePointerDown = (e) => {
    lastPointerType.current = e.pointerType;
  };
  const handleClick = (e) => {
    // A click from Enter or Space has detail 0 and always toggles; a mouse
    // click is left alone because hover already owns the state.
    if (!expandable || (e.detail !== 0 && lastPointerType.current === 'mouse')) return;
    onToggle(headRef.current);
  };

  // With a link, the title is the way out to it, so it can't also be the
  // button that toggles the row. Keyboard focus reveals the details the way
  // hover does, and on touch a first tap reveals them, a second follows.
  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (e.detail !== 0 && lastPointerType.current !== 'mouse' && !isOpen) {
      e.preventDefault();
      onToggle(headRef.current);
    }
  };
  const handleLinkFocus = (e) => {
    if (!e.currentTarget.matches(':focus-visible')) return;
    focusOpened.current = true;
    onOpen();
  };
  const handleLinkBlur = () => {
    if (!focusOpened.current) return;
    focusOpened.current = false;
    onClose();
  };

  const leader = (
    <motion.span
      className="row-leader"
      aria-hidden="true"
      variants={drawIn}
      custom={index}
      style={leaderStyle(reach)}
    />
  );
  const period = <span className="row-period">{periodOf(entry)}</span>;
  // A row with only a header has nothing to open onto: the header still swaps
  // in for the title, but the rows below stay where they are. A row with
  // neither is just a line, with nothing to toggle.
  const hasBody = details.length > 0 || entry.technologies?.length > 0;
  const expandable = !alwaysOpen && (Boolean(header) || hasBody);
  const bodyContent = (
    <>
      {header && !alwaysOpen && <p className="sr-only">{header}</p>}
      {details.length > 0 && (
        <ul className="row-bullets">
          {details.map((detail, i) => (
            <li key={i}>{emphasize(detail)}</li>
          ))}
        </ul>
      )}
      {entry.technologies?.length > 0 && (
        <ul className="row-tech" aria-label="technologies">
          {entry.technologies.map((tech) => (
            <li key={tech}>{tech}</li>
          ))}
        </ul>
      )}
    </>
  );

  const Head = expandable ? 'button' : 'div';
  const headProps = expandable
    ? { type: 'button', className: 'row-head', 'aria-expanded': isOpen, 'aria-controls': bodyId }
    : { className: 'row-head row-head--static' };

  return (
    <motion.li
      className={`row${isOpen ? ' is-open' : ''}${entry.starts ? ' row--incoming' : ''}${isCurrent(entry) ? ' row--current' : ''}${arrived ? ' row--arrived' : ''}`}
      data-key={rowKey}
      variants={fadeIn}
      custom={index}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      {entry.link ? (
        <div ref={headRef} className="row-head row-head--static">
          <a
            ref={titleRef}
            className="row-title row-title-link is-external"
            href={entry.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
            onFocus={handleLinkFocus}
            onBlur={handleLinkBlur}
          >
            {/* As in the button rows, the visible text turns into the header
                while open; screen readers always get the title here. */}
            <span className="sr-only">{title} (opens in a new tab)</span>
            <span className="row-link-line" aria-hidden="true">
              <SwapText from={title} to={header} active={isOpen} render={renderLinkText} />
            </span>
            <span ref={endRef} />
          </a>
          {leader}
          {period}
        </div>
      ) : (
        <Head ref={headRef} {...headProps}>
          <span ref={titleRef} className="row-title">
            {/* The visible text turns into the header while open; screen
                readers always get the title here and the header in the body. */}
            <span className="sr-only">{title}</span>
            <span aria-hidden="true">
              <SwapText from={title} to={header} active={isOpen && !alwaysOpen} />
            </span>
            <span ref={endRef} />
          </span>
          {leader}
          {period}
        </Head>
      )}

      {alwaysOpen && (
        <>
          {header && <p className="row-subline">{header}</p>}
          <div className="row-body row-body--static">{bodyContent}</div>
        </>
      )}
      <AnimatePresence initial={false}>
        {isOpen && hasBody && !alwaysOpen && (
          <motion.div
            id={bodyId}
            className="row-body-wrap"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto', transition: { duration: 0.3, delay: 0.05 } }}
            exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
            style={{ overflow: 'hidden' }}
          >
            <div className="row-body">{bodyContent}</div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* A link row has no toggle to announce, so a screen reader reading
          straight past it still gets the details. */}
      {entry.link && !isOpen && <div className="sr-only">{bodyContent}</div>}
    </motion.li>
  );
}

function ExternalIcon() {
  return (
    <svg
      className="row-external"
      viewBox="0 0 12 12"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 2.5H2.5v7h7V7" />
      <path d="M7 2h3v3M10 2 5.5 6.5" />
    </svg>
  );
}

// The visible text of a title that links out. The icon rides with the last
// word, so a title that wraps never leaves it stranded on a line of its own.
function LinkText({ text }) {
  const split = text.lastIndexOf(' ') + 1;
  return (
    <>
      <span className="row-link-text">{text.slice(0, split)}</span>
      <span className="row-title-tail">
        <span className="row-link-text">{text.slice(split)}</span>
        <ExternalIcon />
      </span>
    </>
  );
}

const renderLinkText = (text) => <LinkText text={text} />;

// A row that is just a link out — used for papers.
function LinkRow({ entry, index, subline }) {
  const titleRef = useRef(null);
  const endRef = useRef(null);
  const reach = useLeaderReach(titleRef, endRef);
  return (
    <motion.li className="row row--link" variants={fadeIn} custom={index}>
      <div className="row-head row-head--static">
        <a
          ref={titleRef}
          className="row-title row-title-link is-external"
          href={entry.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="row-link-line">
            <LinkText text={entry.title} />
          </span>
          <span className="sr-only"> (opens in a new tab)</span>
          <span ref={endRef} />
        </a>
        <motion.span
          className="row-leader"
          aria-hidden="true"
          variants={drawIn}
          custom={index}
          style={leaderStyle(reach)}
        />
        <span className="row-period">{entry.period}</span>
      </div>
      {subline && <p className="row-subline">{subline}</p>}
    </motion.li>
  );
}

// A post, as a row of the work list: its title opens it, turning the page.
// The title glides up into the post's heading (useRoute.js).
function PostRow({ post, index, rowKey, subline, arrived = false }) {
  const titleRef = useRef(null);
  const endRef = useRef(null);
  const reach = useLeaderReach(titleRef, endRef);
  const href = `/shower-thoughts/${post.slug}`;
  return (
    <motion.li
      className={`row row--link row--post${arrived ? ' row--arrived' : ''}`}
      data-key={rowKey}
      variants={fadeIn}
      custom={index}
    >
      <div className="row-head row-head--static">
        <a ref={titleRef} className="row-title row-title-link row-title-post" href={href} onClick={linkTo(href)}>
          <span className="row-link-line">
            <span className="row-post-name" data-vt={`post-${post.slug}`}>
              {post.title}
            </span>
          </span>
          <span className="row-post-arrow" aria-hidden="true">
            →
          </span>
          <span ref={endRef} />
        </a>
        <motion.span
          className="row-leader"
          aria-hidden="true"
          variants={drawIn}
          custom={index}
          style={leaderStyle(reach)}
        />
        <span className="row-period">{post.date}</span>
      </div>
      {subline && <p className="row-subline">{subline}</p>}
    </motion.li>
  );
}

const hoverCapable = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function useNarrow(query = '(max-width: 900px)') {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setNarrow(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

// The writing section's two kinds, in the order they're listed, under their
// names: papers ('link') and posts.
const KIND_NAMES = { link: 'publications', post: 'shower thoughts' };

// When an entry began, as a number that sorts: 2026.09 for september 2026.
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const whenOf = (item) => {
  const [month, year] = (item.date ?? item.period).split(' – ')[0].split(' ');
  return Number(year) + (MONTHS.indexOf(month) + 1) / 100;
};

// A post's first sentence, in lowercase like the rest of the site.
const openingOf = (post) => {
  const first = parsePost(post.body.toLowerCase()).find((b) => b.type === 'p')?.text ?? '';
  const end = first.search(/[.?!](\s|$)/);
  return end < 0 ? first : first.slice(0, end + 1);
};

const NOUNS = {
  experience: ['role', 'roles'],
  education: ['school', 'schools'],
  writing: ['piece', 'pieces'],
};
const countOf = (sec) => `${sec.items.length} ${(NOUNS[sec.id] ?? ['', ''])[sec.items.length === 1 ? 0 : 1]}`;

// The section names as tabs, with a marker that slides to the one showing.
// Arrow keys move between them, as tabs should; with `hoverSwitch`, resting
// on one picks it.
function SectionNav({ list, active, onPick, vertical = false, id, hoverSwitch = false }) {
  const refs = useRef([]);
  const navRef = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const keys = vertical ? { ArrowDown: 1, ArrowUp: -1 } : { ArrowRight: 1, ArrowLeft: -1 };

  // A strip that scrolls sideways (on a phone) keeps the tab showing in view.
  // Only the strip scrolls; scrollIntoView would move the page too.
  const activeIndex = list.findIndex((sec) => sec.id === active);
  useEffect(() => {
    const nav = navRef.current;
    const tab = refs.current[activeIndex];
    if (vertical || !nav || !tab || nav.scrollWidth <= nav.clientWidth) return;
    const left = tab.getBoundingClientRect().left - nav.getBoundingClientRect().left + nav.scrollLeft;
    const right = left + tab.offsetWidth;
    if (left < nav.scrollLeft || right > nav.scrollLeft + nav.clientWidth) {
      nav.scrollTo({ left: Math.max(0, left - 16), behavior: 'smooth' });
    }
  }, [activeIndex, vertical]);

  return (
    <div
      ref={navRef}
      className={`work-nav${vertical ? ' work-nav--vertical' : ''}`}
      role="tablist"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
    >
      {list.map((sec, i) => {
        const selected = sec.id === active;
        return (
          <button
            key={sec.id}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="tab"
            id={`${id}-tab-${sec.id}`}
            aria-selected={selected}
            aria-controls={`${id}-panel`}
            tabIndex={selected ? 0 : -1}
            className="work-nav-tab"
            onClick={() => onPick(sec.id)}
            onMouseEnter={
              hoverSwitch
                ? () => {
                    clearTimeout(timer.current);
                    timer.current = setTimeout(() => onPick(sec.id), HOVER_OPEN_DELAY_MS + 20);
                  }
                : undefined
            }
            onMouseLeave={hoverSwitch ? () => clearTimeout(timer.current) : undefined}
            onKeyDown={(e) => {
              const step = keys[e.key];
              if (!step) return;
              e.preventDefault();
              const next = (i + step + list.length) % list.length;
              onPick(list[next].id);
              refs.current[next]?.focus();
            }}
          >
            <span className="work-nav-label">
              {sec.heading}
              {selected && (
                <motion.span
                  layoutId={`${id}-marker`}
                  className="work-nav-marker"
                  aria-hidden="true"
                  transition={{ type: 'spring', stiffness: 520, damping: 42 }}
                />
              )}
            </span>
            <span className="sr-only">, {countOf(sec)}</span>
          </button>
        );
      })}
    </div>
  );
}

// A panel's way in and out: a short slide along `axis` ('x' or 'y') in the
// direction the tabs moved. A panel swiped away (`flung`, where the finger
// left it) carries on out the way it was going, and the next comes in from
// further off to meet it.
const panelMotion = {
  hidden: ({ dir, axis, flung }) => ({ opacity: 0, [axis]: (flung ? 40 : 14) * dir }),
  visible: { opacity: 1, x: 0, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: ({ dir, axis, flung }) =>
    flung
      ? { opacity: 0, x: flung - 40 * dir, transition: { duration: 0.16 } }
      : { opacity: 0, [axis]: -10 * dir, transition: { duration: 0.14 } },
};

// The work page: one section at a time, picked from its name. On a wide
// screen the names run down the left margin (`side`), switching as the
// cursor rests on one; under 900px they're a strip of tabs across the top
// (`tabs`), and the list swipes between them.
export default function Experiences() {
  const colRef = useRef(null);

  // Back from a post on one of its experiences: that row comes into view,
  // marked for a moment, and on a touchscreen, where there's no hovering to
  // read it, open. The page turn has already shown the list, so it skips its
  // entrance.
  const [arrived] = useState(() => ({ ...arrival }));
  useEffect(() => {
    Object.assign(arrival, { key: null, quiet: false });
  }, []);
  const [openKey, setOpenKey] = useState(() => (arrived.key && !hoverCapable() ? arrived.key : null));
  const [markedKey, setMarkedKey] = useState(arrived.key);
  useEffect(() => {
    if (!markedKey) return undefined;
    const timer = setTimeout(() => setMarkedKey(null), 2000);
    return () => clearTimeout(timer);
  }, [markedKey]);

  // Only one row is open at a time. Hover opens and closes rows directly; a
  // tap or Enter toggles one and closes whichever other row was open.
  const openRow = (key) => setOpenKey(key);
  const closeRow = (key) => setOpenKey((current) => (current === key ? null : current));
  const toggleRow = (key, head) => {
    // When the row closing sits above the one tapped, everything below it
    // slides up as it collapses — the tapped row included, sometimes off
    // screen. Hold the tapped row still while that happens.
    if (openKey && openKey !== key) holdInPlace(head);
    setOpenKey(openKey === key ? null : key);
  };
  const canHover = useCanHover();
  const reduceMotion = useReducedMotion();
  const layout = useNarrow() ? 'tabs' : 'side';

  // Which section shows, which way the last switch went, and whether there
  // has been one (the first panel arrives with the page's own entrance; later
  // ones bring themselves in). Back from a post, it's the section of the row
  // it came back to.
  const arrivedIn = sections.find((sec) => arrived.key?.startsWith(`${sec.id}-`))?.id;
  const [tab, setTab] = useState(arrivedIn ?? sections[0].id);
  const [dir, setDir] = useState(1);
  const [switched, setSwitched] = useState(false);
  const [flung, setFlung] = useState(0);
  const pick = (id, from = 0) => {
    if (id === tab) return;
    setDir(sections.findIndex((sec) => sec.id === id) > sections.findIndex((sec) => sec.id === tab) ? 1 : -1);
    setSwitched(true);
    setFlung(from);
    setTab(id);
  };
  const active = sections.find((sec) => sec.id === tab) ?? sections[0];

  // On a touchscreen, the panel swipes to the tab either side. It follows
  // the finger (barely, where there's no tab that way) and springs back,
  // unless it's let go far or fast enough to go on to the next. Dragged
  // sideways only, it leaves the page to scroll as usual. A swipe's lift
  // isn't a tap: the row under the finger stays as it was.
  const swiped = useRef(false);
  const swipe = () => {
    if (canHover) return {};
    const i = sections.indexOf(active);
    const [prev, next] = [sections[i - 1], sections[i + 1]];
    const give = { left: next ? 0.5 : 0.1, right: prev ? 0.5 : 0.1 };
    return {
      drag: 'x',
      dragConstraints: { left: 0, right: 0 },
      dragElastic: give,
      onPointerDown: () => (swiped.current = false),
      onDragStart: () => (swiped.current = true),
      onClickCapture: (e) => {
        if (!swiped.current) return;
        e.stopPropagation();
        e.preventDefault();
      },
      onDragEnd: (e, { offset, velocity }) => {
        // How far it would have gone: a quick flick counts for more than its
        // distance, once it's gone far enough to be meant.
        const reach = offset.x + velocity.x * 0.15;
        const meant = Math.abs(offset.x) > 24;
        const to = !meant ? null : reach < -70 ? next : reach > 70 ? prev : null;
        if (to) pick(to.id, offset.x * (offset.x < 0 ? give.left : give.right));
      },
    };
  };

  // Back from a post, put the row it came back to a little above the middle
  // of the screen, or nearer the top when it's open, to leave room for its
  // details, but no higher than the top of the work page, so the tabs show.
  // This runs inside the page turn's update, so the turn lands on the row
  // where it will stay.
  const broughtIn = useRef(false);
  useLayoutEffect(() => {
    if (!arrived.key || broughtIn.current) return;
    broughtIn.current = true;
    const col = colRef.current;
    const head = col?.querySelector(`[data-key="${arrived.key}"] > .row-head`);
    const page = col?.closest('.work-page');
    const floor = page ? page.getBoundingClientRect().top + window.scrollY : 0;
    if (!head) {
      window.scrollTo(0, floor);
      return;
    }
    const top = head.getBoundingClientRect().top + window.scrollY;
    const from = openKey === arrived.key ? 0.18 : 0.38;
    window.scrollTo(0, Math.max(floor, top - window.innerHeight * from));
    (head.matches('a, button') ? head : head.querySelector('a, button'))?.focus({ preventScroll: true });
  }, [arrived.key, openKey]);

  // Running position of each animated element down the page.
  let position = 0;

  const rowProps = (section, i) => {
    const key = `${section.id}-${i}`;
    return {
      rowKey: key,
      isOpen: openKey === key,
      onOpen: () => openRow(key),
      onClose: () => closeRow(key),
      onToggle: (head) => toggleRow(key, head),
      arrived: markedKey === key,
    };
  };

  const renderGroups = (section) =>
    groupRoles(section.items).map(({ company, members }) => {
      if (members.length === 1) {
        const { item, i } = members[0];
        const solo = section.items.length === 1;
        return <Row key={i} entry={item} index={position++} {...rowProps(section, i)} alwaysOpen={solo} />;
      }
      return (
        <motion.li key={`group-${members[0].i}`} className="row-group" variants={fadeIn} custom={position++}>
          <div className="row-head row-head--static row-group-head">
            <span className="row-title">{company}</span>
          </div>
          <ul className="rows row-group-roles">
            {members.map(({ item, i, role }) => (
              <Row key={i} entry={item} index={position++} {...rowProps(section, i)} title={role} />
            ))}
          </ul>
        </motion.li>
      );
    });

  // Writing: papers, then posts, each kind under its name and newest first.
  // Under each title is a line, as the degree sits under education's: a
  // paper's venue, a post's first sentence.
  const renderWriting = (section) => {
    const items = section.items.map((item, i) => ({ item, i })).sort((a, b) => whenOf(b.item) - whenOf(a.item));
    return Object.keys(KIND_NAMES).flatMap((kind) => {
      const ofKind = items.filter(({ item }) => item.kind === kind);
      if (!ofKind.length) return [];
      return [
        <motion.li key={`kind-${kind}`} className="work-kind" variants={fadeIn} custom={position++}>
          {KIND_NAMES[kind]}
        </motion.li>,
        ...ofKind.map(({ item, i }) => {
          if (kind === 'link') {
            return <LinkRow key={`${section.id}-${i}`} entry={item} index={position++} subline={item.description} />;
          }
          const key = `${section.id}-${item.slug}`;
          return (
            <PostRow key={key} post={item} index={position++} rowKey={key} subline={openingOf(item)} arrived={markedKey === key} />
          );
        }),
      ];
    });
  };

  const rowsOf = (section) => (
    <ul className="rows">{section.kind === 'writing' ? renderWriting(section) : renderGroups(section)}</ul>
  );

  // The section's panel. The first arrives with the page's own entrance;
  // after a switch, each new one slides in by itself, its rows counting their
  // stagger from zero.
  const panel = (content, gestures = {}) => {
    if (switched) position = 0;
    const motionOf = { dir, axis: layout === 'side' ? 'y' : 'x', flung };
    return (
      <AnimatePresence mode="wait" initial={false} custom={motionOf}>
        <motion.div
          key={active.id}
          id="work-panel"
          role="tabpanel"
          aria-labelledby={`work-tab-${active.id}`}
          className="work-panel"
          variants={panelMotion}
          custom={motionOf}
          exit="exit"
          {...(switched ? { initial: 'hidden', animate: 'visible' } : {})}
          {...gestures}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    );
  };

  let body;
  if (layout === 'tabs') {
    const navPosition = position++;
    body = (
      <>
        <motion.div className="work-nav-row" variants={fadeIn} custom={navPosition}>
          <SectionNav list={sections} active={active.id} onPick={pick} id="work" />
          <motion.span className="work-rule" aria-hidden="true" variants={drawIn} custom={navPosition} />
        </motion.div>
        {panel(rowsOf(active), swipe())}
      </>
    );
  } else {
    // The names down the left margin, staying put as the list scrolls; under
    // the list, while the section showing has rows to open, a word on how.
    const opens = !active.kind && active.items.length > 1;
    body = (
      <div className="work-side">
        <motion.div className="work-side-nav" variants={fadeIn} custom={position++}>
          <div className="work-side-nav-inner">
            <SectionNav list={sections} active={active.id} onPick={pick} vertical id="work" hoverSwitch={canHover} />
          </div>
        </motion.div>
        <div className="work-side-main">
          {panel(
            <>
              {rowsOf(active)}
              <p className={`work-end-hint${opens ? '' : ' is-hidden'}`}>
                {canHover ? 'hover for details' : 'tap for details'}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="work">
      {/* Like the home screen, this replays each time it scrolls into view.
          Crossing between the two layouts mounts a fresh column, which plays
          again. */}
      <motion.div
        key={layout}
        ref={colRef}
        className="work-col"
        data-layout={layout}
        variants={{ hidden: {}, visible: {} }}
        initial={reduceMotion || arrived.quiet ? false : 'hidden'}
        whileInView="visible"
        viewport={{ once: false, amount: 0.2 }}
      >
        {body}
      </motion.div>
    </div>
  );
}
